import type { ClearanceVertex } from './types';

/**
 * The client-side copy of the clearance-profile validation rules, plus the two
 * parametric vertex generators.
 *
 * Deliberately a third copy of `clearanceValidation.ts` (server) and
 * `clearance_profile.py` (worker): the client's job is to keep the Run button
 * from ever being armed on a profile the other two would reject, with the
 * message shown next to the field rather than in a toast after a round-trip.
 * Change one, change all three — including the ORDER of the checks, which is
 * itself observable (see the self-intersection/area note below).
 *
 * Two deliberate, audited deltas against the Python copy — do NOT "fix" one
 * side to match the other:
 *
 *  1. WORDING. Only the self-intersection message is byte-identical across all
 *     three (`clearance_profile.py` claims byte-identity for that one alone).
 *     The rest read as sentences here and in `clearanceValidation.ts` — which
 *     ARE byte-identical to each other, since both are shown to the same user
 *     in the same dialog — while Python emits lowercase fragments it splices
 *     into an exit-code-4 line, writes `+/-100` for `±100`, and prints an
 *     explicit `+` sign that `toFixed(3)` does not.
 *  2. EARLY-CHECK ORDER. Python validates the whole array's finiteness before
 *     the offset bound and the array's shape before its vertex count; the two
 *     TS copies interleave both per-vertex in one loop. A fixture with two
 *     faults at once (`[[200, 0], [NaN, 1], [0, 3]]`) is therefore reported as
 *     non-finite by the worker and as out-of-range by either TS copy. All
 *     three still REJECT it, the two TS copies still agree with each other,
 *     and the client refuses it long before the worker ever sees it.
 */

export const MIN_PROFILE_VERTICES = 3;
export const MAX_PROFILE_VERTICES = 64;
export const MAX_ABS_OFFSET_M = 100;
/**
 * Finding F-1: `heightM` was uncapped while `offsetM` was capped at
 * ±100 m — the same "a profile wider than a 200 m corridor is a units
 * mistake, not a gauge" argument `clearance_profile.py` makes for the offset
 * cap applies to height too (a typo'd `5030` instead of `5.03` produces a
 * 5 km-tall envelope every validator would otherwise accept). Symmetric with
 * `MAX_ABS_OFFSET_M` on purpose — there is no published profile anywhere
 * near this tall.
 */
export const MAX_ABS_HEIGHT_M = 100;
const EPS = 1e-9;

/**
 * The machine-readable half of a validation failure (finding I11).
 *
 * The English `message` on the thrown error stays byte-for-byte what it was —
 * it is the string the server validator and `clearance_profile.py` are kept
 * identical to, and a run rejected by the worker still surfaces it verbatim.
 * The `code` + its named parameters are what the UI translates, so a French
 * user is no longer told "The profile crosses itself: edges 1 and 3 intersect"
 * in English next to five translated field labels.
 *
 * Adding a case here means adding `clearance.profile.issues.<code>` to all five
 * locale bundles; `clearanceLocales.test.ts` pins the key set.
 */
export type ProfileIssue =
  | { code: 'notAList' }
  // `got`, not `count`: `count` is i18next's PLURAL trigger, and a message
  // interpolating it would silently start resolving through `_one` / `_other`
  // siblings that do not exist.
  | { code: 'tooFew'; min: number; got: number }
  | { code: 'tooMany'; max: number; got: number }
  | { code: 'notAPair'; index: number }
  | { code: 'notFinite'; index: number }
  | { code: 'offsetOutOfRange'; index: number; max: number; offset: string }
  | { code: 'heightOutOfRange'; index: number; max: number; height: string }
  | { code: 'closedRing' }
  | { code: 'duplicateVertex'; a: number; b: number }
  | { code: 'selfIntersects'; a: number; b: number }
  | { code: 'noArea' };

/** An `Error` that also carries the structured reason. `message` is unchanged. */
export class ProfileValidationError extends Error {
  readonly issue: ProfileIssue;
  constructor(issue: ProfileIssue, message: string) {
    super(message);
    this.name = 'ProfileValidationError';
    this.issue = issue;
  }
}

/**
 * A `function` declaration, not a `const` arrow: TypeScript only applies
 * never-returning control-flow narrowing to a call whose callee is a function
 * declaration or an explicitly annotated const, and that narrowing is what lets
 * the loop below keep treating `o`/`h` as numbers after the finite check.
 */
function fail(issue: ProfileIssue, message: string): never {
  throw new ProfileValidationError(issue, message);
}

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

type Pt = readonly [number, number];

const orient = (a: Pt, b: Pt, c: Pt): number => {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  if (v > EPS) return 1;
  if (v < -EPS) return -1;
  return 0;
};

const onSegment = (a: Pt, b: Pt, c: Pt): boolean =>
  Math.min(a[0], b[0]) - EPS <= c[0] &&
  c[0] <= Math.max(a[0], b[0]) + EPS &&
  Math.min(a[1], b[1]) - EPS <= c[1] &&
  c[1] <= Math.max(a[1], b[1]) + EPS;

const same = (a: Pt, b: Pt): boolean => Math.abs(a[0] - b[0]) < EPS && Math.abs(a[1] - b[1]) < EPS;

const segmentsIntersect = (p1: Pt, p2: Pt, p3: Pt, p4: Pt): boolean => {
  const o1 = orient(p1, p2, p3);
  const o2 = orient(p1, p2, p4);
  const o3 = orient(p3, p4, p1);
  const o4 = orient(p3, p4, p2);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p2, p3) && !same(p3, p1) && !same(p3, p2)) return true;
  if (o2 === 0 && onSegment(p1, p2, p4) && !same(p4, p1) && !same(p4, p2)) return true;
  if (o3 === 0 && onSegment(p3, p4, p1) && !same(p1, p3) && !same(p1, p4)) return true;
  if (o4 === 0 && onSegment(p3, p4, p2) && !same(p2, p3) && !same(p2, p4)) return true;
  return false;
};

export const validateProfileVertices = (vertices: unknown): ClearanceVertex[] => {
  if (!Array.isArray(vertices)) fail({ code: 'notAList' }, 'The profile must be a list of vertices');
  if (vertices.length < MIN_PROFILE_VERTICES)
    fail(
      { code: 'tooFew', min: MIN_PROFILE_VERTICES, got: vertices.length },
      `A profile needs at least ${MIN_PROFILE_VERTICES} vertices, got ${vertices.length}`,
    );
  if (vertices.length > MAX_PROFILE_VERTICES)
    fail(
      { code: 'tooMany', max: MAX_PROFILE_VERTICES, got: vertices.length },
      `A profile may have at most ${MAX_PROFILE_VERTICES} vertices, got ${vertices.length}`,
    );

  const poly: ClearanceVertex[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const v: unknown = vertices[i];
    if (!Array.isArray(v) || v.length !== 2)
      fail({ code: 'notAPair', index: i }, `Profile vertex ${i} must be a pair [offsetM, heightM]`);
    const [o, h] = v as unknown[];
    if (!isFiniteNumber(o) || !isFiniteNumber(h))
      fail({ code: 'notFinite', index: i }, `Profile vertex ${i} must be two finite numbers`);
    if (Math.abs(o) > MAX_ABS_OFFSET_M)
      fail(
        { code: 'offsetOutOfRange', index: i, max: MAX_ABS_OFFSET_M, offset: o.toFixed(3) },
        `Profile offsets must be within ±${MAX_ABS_OFFSET_M} m; vertex ${i} is at ${o.toFixed(3)} m`,
      );
    if (Math.abs(h) > MAX_ABS_HEIGHT_M)
      fail(
        { code: 'heightOutOfRange', index: i, max: MAX_ABS_HEIGHT_M, height: h.toFixed(3) },
        `Profile heights must be within ±${MAX_ABS_HEIGHT_M} m; vertex ${i} is at ${h.toFixed(3)} m`,
      );
    poly.push([o, h]);
  }

  if (same(poly[0], poly[poly.length - 1]))
    fail({ code: 'closedRing' }, 'The profile is implicitly closed — drop the repeated final vertex');
  for (let i = 0; i < poly.length; i++) {
    const j = (i + 1) % poly.length;
    if (same(poly[i], poly[j]))
      fail(
        { code: 'duplicateVertex', a: i, b: j },
        `Profile vertices ${i} and ${j} are the same point (zero-length edge)`,
      );
  }

  // Self-intersection is checked BEFORE the area gate, exactly as
  // `clearance_profile.py :: validate_profile` does: a self-crossing polygon's
  // shoelace area can cancel to ~0 even though its bounding box is fine (a
  // bowtie is precisely that), so testing area first would misreport a
  // crossing profile as "no area" instead of naming the offending edges.
  for (let i = 0; i < poly.length; i++) {
    const a1 = poly[i];
    const a2 = poly[(i + 1) % poly.length];
    for (let j = i + 1; j < poly.length; j++) {
      if (j === i || (j + 1) % poly.length === i || (i + 1) % poly.length === j) continue;
      if (segmentsIntersect(a1, a2, poly[j], poly[(j + 1) % poly.length]))
        fail({ code: 'selfIntersects', a: i, b: j }, `The profile crosses itself: edges ${i} and ${j} intersect`);
    }
  }

  // True (shoelace) area, not the axis-aligned bounding-box area: vertices
  // collinear along a diagonal — (0,0), (1,1), (2,2) — enclose nothing yet
  // have a non-zero bbox, and the worker's `signed_distance_to_polygon` would
  // then return a plausible-looking but meaningless number. The tolerance is
  // relative to the bbox so a genuinely thin-but-real sliver still passes.
  const b = profileBounds(poly);
  const bboxArea = (b.maxOffset - b.minOffset) * (b.maxHeight - b.minHeight);
  let shoelace = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x0, y0] = poly[i];
    const [x1, y1] = poly[(i + 1) % poly.length];
    shoelace += x0 * y1 - y0 * x1;
  }
  shoelace *= 0.5;
  if (Math.abs(shoelace) <= Math.max(1e-6, 1e-9 * bboxArea))
    fail({ code: 'noArea' }, 'The profile polygon has no area — the vertices are collinear');

  return poly;
};

/** The same rules, as a message-or-null — the shape a form field needs. */
export const profileError = (vertices: unknown): string | null => {
  try {
    validateProfileVertices(vertices);
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'Invalid profile';
  }
};

/**
 * The same rules, as a TRANSLATABLE reason-or-null (finding I11).
 *
 * `profileError` stays for anything that wants the canonical English sentence
 * (the cross-validator contract, logs, the payload guard); the UI reads this
 * and renders `clearance.profile.issues.<code>` instead. A throw that somehow
 * carried no code degrades to `notAList`'s generic wording rather than to a
 * blank field.
 */
export const profileIssue = (vertices: unknown): ProfileIssue | null => {
  try {
    validateProfileVertices(vertices);
    return null;
  } catch (err) {
    return err instanceof ProfileValidationError ? err.issue : { code: 'notAList' };
  }
};

export const profileBounds = (
  vertices: readonly ClearanceVertex[],
): { minOffset: number; maxOffset: number; minHeight: number; maxHeight: number } => {
  let minOffset = Infinity;
  let maxOffset = -Infinity;
  let minHeight = Infinity;
  let maxHeight = -Infinity;
  for (const [o, h] of vertices) {
    if (o < minOffset) minOffset = o;
    if (o > maxOffset) maxOffset = o;
    if (h < minHeight) minHeight = h;
    if (h > maxHeight) maxHeight = h;
  }
  return { minOffset, maxOffset, minHeight, maxHeight };
};

/**
 * Presets are vertex GENERATORS, not rows in `clearance_profiles` (spec §1):
 * a preset that lived in the database would have to be seeded per
 * organisation, could be edited into something that no longer matches its
 * name, and would still need this code to regenerate it when a parameter
 * changed.
 *
 * Every generator emits counter-clockwise vertices starting at the
 * bottom-left, which is the order the sketch's dimension lines assume.
 *
 * The six shipped STANDARD presets (TC E-05 Diagrams 1 and 3, California
 * 8 CCR, ARTC ABCD and F, and the named road-box heights) live in
 * `presets.ts`, not here: this file is the rules plus the two parametric
 * generators, and it must stay importable by the validator tests without
 * dragging in a table of published dimensions.
 */
export const rectangleProfile = ({
  widthM,
  heightM,
  baseM,
}: {
  widthM: number;
  heightM: number;
  baseM: number;
}): ClearanceVertex[] => {
  const half = widthM / 2;
  return [
    [-half, baseM],
    [half, baseM],
    [half, baseM + heightM],
    [-half, baseM + heightM],
  ];
};

export const trapezoidProfile = ({
  bottomWidthM,
  topWidthM,
  heightM,
  baseM,
}: {
  bottomWidthM: number;
  topWidthM: number;
  heightM: number;
  baseM: number;
}): ClearanceVertex[] => {
  const b = bottomWidthM / 2;
  const t = topWidthM / 2;
  return [
    [-b, baseM],
    [b, baseM],
    [t, baseM + heightM],
    [-t, baseM + heightM],
  ];
};
